"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="207510" className="flex gap-4">
        <div className="h-9 w-20 animate-pulse rounded-lg bg-muted" />
        <div className="h-9 w-20 animate-pulse rounded-lg bg-muted" />
        <div className="h-9 w-20 animate-pulse rounded-lg bg-muted" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex h-9 w-max items-center justify-center rounded-lg bg-transparent px-4 py-2 text-[14px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#e6cd93] hover:text-white focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [currentPath, setCurrentPath] = useState("");
  const [currentHash, setCurrentHash] = useState("");
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentPath(window.location.pathname);
      setCurrentHash(window.location.hash);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 p-4 shadow-lg backdrop-blur-md md:hidden">
      <nav className="flex flex-col space-y-4">
        {loading ? <>
            <div className="h-10 w-full animate-pulse rounded-lg bg-muted" />
            <div className="h-10 w-full animate-pulse rounded-lg bg-muted" />
            <div className="h-10 w-full animate-pulse rounded-lg bg-muted" />
          </> : menuItems.map((item, i) => {
        const itemHref = item.href || "#";
        const isActive = itemHref === currentPath || itemHref === `${currentPath}${currentHash}` || itemHref.startsWith("#") && itemHref === currentHash;
        return <div key={item.id} data-index={i}>
                <Link to={itemHref} className={`block px-4 py-2 text-base font-bold uppercase tracking-widest transition-colors duration-300 hover:text-[#D9C28E] ${isActive ? "text-[#D9C28E] border-b-2 border-[#D9C28E]" : "text-[#F5F5F5]"}`} onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>
              </div>;
      })}
        <div className="pt-4">
          <Button className="w-full bg-[#e6cd93] text-[#ffffff] hover:bg-[#e6cd93] hover:text-[#ffffff] shadow-md transition-all duration-300 rounded-lg font-sans font-bold uppercase tracking-widest text-[14px] py-6">
            Contactanos
          </Button>
        </div>
      </nav>
    </div>;
}
export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="cabecera-principal" className="sticky top-0 z-50 w-full transition-all duration-300 !bg-[hsl(203,55%,13%)]">
      <style dangerouslySetInnerHTML={{
      __html: `
          #cabecera-principal form label,
          #cabecera-principal form input,
          #cabecera-principal form textarea {
            color: #ffffff !important;
          }
          #cabecera-principal form input::placeholder,
          #cabecera-principal form textarea::placeholder {
            color: #ffffff !important;
            opacity: 1;
          }
        `
    }} />
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
        <div className="flex items-center">
          <Link to="/" className="flex items-center gap-2 transition-opacity hover:opacity-90">
            <WvcLogo className="h-[60px] w-auto" />
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="44">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu" className="text-[#ffffff] hover:bg-[#e6cd93] hover:text-[#ffffff] rounded-lg">
            {isMobileMenuOpen ? <X className="size-6 text-primary" /> : <Menu className="size-6 text-primary" />}
          </Button>
        </div>
      </div>

      <MenuProvider menu_id="44">
        <MobileMenuConsumer isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </section>;
}